/**
 * What the events hub keeps on the device (feature-local KV keys, scoped to the signed-in
 * player): a brief of the events last fetched (the rail's countdown chip and "new event" dot,
 * and the ended event whose results the hub shows once /v1/events/current no longer lists it),
 * the stars earned per event level (the API keeps only the event total), and the server clock
 * offset. Tiny and synchronous: the manifest reads it.
 */
import type { AppContext } from '../../game/context.js';

type Ctx = Pick<AppContext, 'services'>;

/** event rounds kept while offline (pending.ts); read here too, for the map hook's check */
export const PENDING_KEY = 'feature.events.pending';
const CACHE = 'feature.events.cache';
const CLOCK = 'feature.events.clock';
const STARS = 'feature.events.stars';

/** An ended event's results stay reachable in the hub this long. */
export const ENDED_KEEP_MS = 14 * 86_400_000;

export interface EventBrief {
  id: string;
  name: string;
  version: number;
  startsAt: number;
  endsAt: number;
  points: number;
  /** the hub was opened since this event (version) went live */
  seen: boolean;
}

interface Cache {
  playerId: string | null;
  fetchedAt: number;
  events: EventBrief[];
}

interface ClockOffset {
  offset: number;
  at: number;
}

function read<T>(a: Ctx, key: string): T | null {
  try {
    const raw = a.services.kv.get(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(a: Ctx, key: string, v: unknown): void {
  try {
    a.services.kv.set(key, JSON.stringify(v));
  } catch {
    /* storage full or blocked: the next load fetches again */
  }
}

export function playerId(a: Ctx): string | null {
  return a.services.store.state.auth?.playerId ?? null;
}

function cache(a: Ctx): Cache | null {
  const c = read<Cache>(a, CACHE);
  return c && Array.isArray(c.events) && c.playerId === playerId(a) ? c : null;
}

export function cachedEvents(a: Ctx): EventBrief[] {
  return cache(a)?.events ?? [];
}

export function cacheFetchedAt(a: Ctx): number | null {
  return cache(a)?.fetchedAt ?? null;
}

/**
 * Store the live list just fetched: live events keep their `seen` mark (same version), ended
 * ones stay for their results until ENDED_KEEP_MS after the end.
 */
export function rememberEvents(a: Ctx, live: Omit<EventBrief, 'seen'>[], now: number, seen = false): EventBrief[] {
  const old = cachedEvents(a);
  const ids = new Set(live.map((e) => e.id));
  const kept = old.filter((e) => !ids.has(e.id) && e.endsAt > now - ENDED_KEEP_MS).map((e) => (e.endsAt > now ? { ...e, endsAt: Math.min(e.endsAt, now) } : e));
  const fresh = live.map((e) => {
    const prev = old.find((o) => o.id === e.id && o.version === e.version);
    return { ...e, seen: seen || (prev?.seen ?? false) };
  });
  const events = [...fresh, ...kept];
  write(a, CACHE, { playerId: playerId(a), fetchedAt: a.services.now(), events } satisfies Cache);
  return events;
}

export function setCachedPoints(a: Ctx, id: string, points: number): void {
  const c = cache(a);
  if (!c) return;
  write(a, CACHE, { ...c, events: c.events.map((e) => (e.id === id ? { ...e, points } : e)) });
}

/** Live events by the cache (soonest end first). */
export function liveBriefs(a: Ctx, now: number): EventBrief[] {
  return cachedEvents(a)
    .filter((e) => e.startsAt <= now && now < e.endsAt)
    .sort((x, y) => x.endsAt - y.endsAt);
}

/** The most recently ended event the player took part in (results in the hub). */
export function lastEnded(a: Ctx, now: number): EventBrief | null {
  return (
    cachedEvents(a)
      .filter((e) => e.endsAt <= now && e.endsAt > now - ENDED_KEEP_MS && e.points > 0)
      .sort((x, y) => y.endsAt - x.endsAt)[0] ?? null
  );
}

/** Rounds of this player wait to be sent (or to be reported as not counted). */
export function roundsWaiting(a: Ctx): boolean {
  const list = read<unknown>(a, PENDING_KEY);
  const me = playerId(a);
  return Array.isArray(list) && list.some((p: { playerId?: unknown } | null) => p?.playerId === me);
}

export function serverNow(a: Ctx): number {
  const c = read<ClockOffset>(a, CLOCK);
  return a.services.now() + (c && Number.isFinite(c.offset) ? c.offset : 0);
}

export function clockAt(a: Ctx): number | null {
  const c = read<ClockOffset>(a, CLOCK);
  return c && Number.isFinite(c.at) ? c.at : null;
}

export function saveClock(a: Ctx, offset: number, at: number): void {
  write(a, CLOCK, { offset, at } satisfies ClockOffset);
}

type Stars = { playerId: string | null; byEvent: Record<string, Record<string, number>> };

/** Best stars per level index of an event, as played on this device (0 = played, lost). */
export function levelStars(a: Ctx, eventId: string): Record<string, number> {
  const s = read<Stars>(a, STARS);
  return s && s.playerId === playerId(a) ? (s.byEvent[eventId] ?? {}) : {};
}

export function saveLevelStars(a: Ctx, eventId: string, idx: number, stars: number, keep: readonly string[]): void {
  const s = read<Stars>(a, STARS);
  const byEvent = s && s.playerId === playerId(a) ? s.byEvent : {};
  const cur = byEvent[eventId] ?? {};
  const next: Stars['byEvent'] = { [eventId]: { ...cur, [idx]: Math.max(stars, cur[idx] ?? 0) } };
  // only events still listed are worth keeping
  for (const id of keep) if (id !== eventId && byEvent[id]) next[id] = byEvent[id]!;
  write(a, STARS, { playerId: playerId(a), byEvent: next } satisfies Stars);
}
